/**
 * Ownership and a safety net for "orbit is parked".
 *
 * ## The problem this solves
 *
 * `OrbitControls.enabled` is a raw mutable flag, and roughly fifteen call sites
 * across the tools flip it to `false` for the duration of some interaction — a
 * brush stroke, a lasso, a gizmo drag, a ring pick, painting mode — then flip it
 * back when that interaction ends. Nothing owns the flag and nothing coordinates
 * those writers, so every one of them is a place orbit can get stranded:
 *
 *   - the "put it back" half runs on a pointer-up that never arrives (released
 *     outside the window, pointercancel, the browser stealing the pointer);
 *   - the tool is unmounted mid-interaction, which is what "Discard and go back"
 *     does to every tool on the step at once;
 *   - two writers overlap and the second one's restore runs before the first's.
 *
 * The flag then reads `false` with nothing holding it, and there is no tool left
 * on screen to ever set it back. Orbit is dead until reload.
 *
 * ## The model
 *
 * A park is *held by an owner*. `parkOrbit('sculpt-brush')` takes a hold,
 * `releaseOrbit('sculpt-brush')` drops it, and orbit is disabled exactly while
 * at least one hold exists. Holds are idempotent and independent, so two tools
 * parking at once cannot clobber each other's restore.
 *
 * `releaseAllOrbitParks()` drops every hold at once. That is what a navigation
 * wants: it is tearing down every tool on the step, so by definition no tool is
 * still interacting.
 *
 * ## The safety net
 *
 * Converting all fifteen legacy writers is not required for this to work.
 * `enforceOrbitEnabled` runs once a frame and repairs the invariant: if orbit is
 * disabled while nothing legitimately holds it, it is re-enabled. "Legitimately
 * holds it" is one of
 *
 *   - a registered park (above), or
 *   - a pointer button is currently down — every legacy writer parks orbit for
 *     the duration of a drag, and a drag by definition has a button held.
 *
 * So a legacy writer that leaks its restore self-heals a few frames later
 * instead of bricking the viewport, and the leak is reported once so it stays
 * visible rather than silently papered over.
 *
 * Only `enabled` is governed. `enableRotate` is deliberately off for the whole
 * lifetime of the axes fine-tune ortho view, which is a legitimate long-lived
 * state rather than an interaction, and healing it would break that view.
 */

/** Owners currently holding orbit parked. */
const parks = new Set();

/** Pointer buttons currently held down anywhere in the document. */
let pointersDown = 0;

/** When the invariant first went unheld, or 0 if it currently holds. */
let unheldSince = 0;

/** Owners already reported as leaking, so the warning fires once per owner. */
const reported = new Set();

/**
 * Grace period before repairing. A frame or two of "disabled with no holder" is
 * normal while a pointer-up and a React commit race each other; a real leak
 * stays unheld indefinitely.
 */
const HEAL_AFTER_MS = 250;

/**
 * Take a hold on orbit. Disables it immediately.
 *
 * @param {string} owner Stable id for the interaction, e.g. 'measure-ring-pick'.
 * @param {import('three/examples/jsm/controls/OrbitControls.js').OrbitControls} [controls]
 */
export function parkOrbit(owner, controls) {
  parks.add(owner);
  unheldSince = 0;
  if (controls) controls.enabled = false;
}

/**
 * Drop this owner's hold. Re-enables orbit if it was the last one.
 *
 * @param {string} owner
 * @param {import('three/examples/jsm/controls/OrbitControls.js').OrbitControls} [controls]
 */
export function releaseOrbit(owner, controls) {
  if (!parks.delete(owner)) return;
  if (controls && parks.size === 0) controls.enabled = true;
}

/**
 * Drop every hold. For navigation and other wholesale teardowns, where all tools
 * are going away and none of them can still be mid-interaction.
 *
 * @param {import('three/examples/jsm/controls/OrbitControls.js').OrbitControls} [controls]
 */
export function releaseAllOrbitParks(controls) {
  parks.clear();
  unheldSince = 0;
  reported.clear();
  if (controls) controls.enabled = true;
}

/** @returns {boolean} true while any owner holds orbit parked. */
export function isOrbitParked() {
  return parks.size > 0;
}

/**
 * Track pointer buttons so the safety net can tell an in-flight drag from a
 * leaked one. Listeners are on `window` in the capture phase so a tool calling
 * `stopPropagation` cannot hide a pointer-up from us, and `blur` covers a button
 * released outside the browser, which fires no pointer-up at all.
 *
 * @returns {() => void} uninstall
 */
export function installOrbitPointerTracking() {
  const down = () => { pointersDown += 1; };
  const up = () => { pointersDown = Math.max(0, pointersDown - 1); };
  const clear = () => { pointersDown = 0; };

  window.addEventListener('pointerdown', down, true);
  window.addEventListener('pointerup', up, true);
  window.addEventListener('pointercancel', clear, true);
  window.addEventListener('blur', clear);

  return () => {
    window.removeEventListener('pointerdown', down, true);
    window.removeEventListener('pointerup', up, true);
    window.removeEventListener('pointercancel', clear, true);
    window.removeEventListener('blur', clear);
    clear();
  };
}

/**
 * Repair the invariant: orbit stays disabled only while something holds it.
 * Call once per frame from the render loop.
 *
 * @param {import('three/examples/jsm/controls/OrbitControls.js').OrbitControls} controls
 */
export function enforceOrbitEnabled(controls) {
  if (!controls || controls.enabled) {
    unheldSince = 0;
    return;
  }

  // Held for a real reason — a registered park, or a drag still in flight.
  if (parks.size > 0 || pointersDown > 0) {
    unheldSince = 0;
    return;
  }

  const now = performance.now();
  if (unheldSince === 0) {
    unheldSince = now;
    return;
  }
  if (now - unheldSince < HEAL_AFTER_MS) return;

  unheldSince = 0;
  controls.enabled = true;

  // Name it once so a leaking writer gets fixed at the source rather than
  // living on behind the safety net.
  if (!reported.has('leak')) {
    reported.add('leak');
    console.warn(
      '[orbitControlsGuard] orbit was disabled with no pointer down and no ' +
        'registered park — re-enabling. Some tool disabled OrbitControls and ' +
        'never restored it; convert it to parkOrbit/releaseOrbit.',
    );
  }
}
